import { useState, useEffect, useRef, useMemo } from 'react'
import { Check, ChevronDown, ChevronsUpDown, MapPin, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { FilterSearchInput } from '@/components/shared/FilterSearchInput'
import {
  filterDropdownTriggerClassName,
  filterDropdownValueClassName,
  filterLocationPanelClassName,
  filterLocationPanelListItemClassName,
  filterLocationPanelSearchInputClassName,
  filterSearchBarIconClassName,
  filterSheetSelectContentSideOffset,
} from '@/components/shared/filterSheetStyles'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { getAllCities, loadWorldCities, isWorldCitiesLoaded } from '@/lib/worldCities'

export interface CityData {
  city: string
  cityAscii: string
  latitude: number
  longitude: number
  country: string
  iso2: string
  iso3: string
  population: number
}

export type CitySelectorDropdownMode = 'auto' | 'search' | 'compact'
export type CitySelectorLayout = 'popover' | 'inline'
export type CitySelectorVariant = 'default' | 'filterSheet'

interface CitySelectorProps {
  /** Trigger label (single select: selected city; multi: summary e.g. "All locations"). */
  value: string
  onChange?: (cityName: string, cityData: CityData | null) => void
  filterCities?: string[]
  placeholder?: string
  className?: string
  disabled?: boolean
  showLocationIcon?: boolean
  /** `search` always shows the search field; `compact` is a short list without search. */
  dropdownMode?: CitySelectorDropdownMode
  /** `inline` expands below the trigger (filter sheet); `popover` overlays with absolute positioning. */
  layout?: CitySelectorLayout
  variant?: CitySelectorVariant
  multiSelect?: boolean
  selectedCities?: string[]
  onMultiChange?: (cities: string[]) => void
  searchPlaceholder?: string
  /** When set (e.g. from ArtistsDataContext), launched-city lists sort by eligible artist count. */
  artistCountByCity?: Record<string, number>
}

const COMPACT_DROPDOWN_CITY_LIMIT = 6

function sortCityDataByArtistCount(
  cities: CityData[],
  artistCountByCity: Record<string, number> | undefined,
): CityData[] {
  if (!artistCountByCity || Object.keys(artistCountByCity).length === 0) {
    return cities
  }
  return [...cities].sort((a, b) => {
    const countDifference = (artistCountByCity[b.city] ?? 0) - (artistCountByCity[a.city] ?? 0)
    if (countDifference !== 0) return countDifference
    return a.city.localeCompare(b.city)
  })
}

export function CitySelector({
  value,
  onChange,
  filterCities,
  placeholder = 'Choose a city',
  className = '',
  disabled = false,
  showLocationIcon = false,
  dropdownMode = 'auto',
  layout = 'popover',
  variant = 'default',
  multiSelect = false,
  selectedCities = [],
  onMultiChange,
  searchPlaceholder = 'Search cities...',
  artistCountByCity,
}: CitySelectorProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoadingCities, setIsLoadingCities] = useState(true)
  const [allCitiesData, setAllCitiesData] = useState<CityData[]>([])
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isFilterSheetVariant = variant === 'filterSheet'

  useEffect(() => {
    const loadCities = async () => {
      try {
        if (!isWorldCitiesLoaded()) {
          await loadWorldCities()
        }
        setAllCitiesData(getAllCities())
      } catch (error) {
        console.error('[CitySelector] Error loading cities:', error)
      } finally {
        setIsLoadingCities(false)
      }
    }

    void loadCities()
  }, [])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
        setSearchQuery('')
      }
    }

    const usePortaledFilterSheetPopover = isFilterSheetVariant && layout === 'popover'
    if (isDropdownOpen && layout === 'popover' && !usePortaledFilterSheetPopover) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen, isFilterSheetVariant, layout])

  const availableCities = useMemo(() => {
    if (filterCities && filterCities.length > 0) {
      return allCitiesData.filter((cityData) => filterCities.includes(cityData.city))
    }
    return allCitiesData
  }, [allCitiesData, filterCities])

  const cityCountForLayout = filterCities?.length ?? availableCities.length
  const useCompactDropdown =
    dropdownMode === 'compact' ||
    (dropdownMode === 'auto' &&
      cityCountForLayout > 0 &&
      cityCountForLayout <= COMPACT_DROPDOWN_CITY_LIMIT)
  const useSearchDropdown = dropdownMode === 'search' || !useCompactDropdown

  const compactCities = useMemo(() => {
    let list: CityData[]
    if (filterCities && filterCities.length > 0) {
      list = filterCities
        .map((cityName) => availableCities.find((cityData) => cityData.city === cityName))
        .filter((cityData): cityData is CityData => Boolean(cityData))
    } else {
      list = [...availableCities]
    }

    if (artistCountByCity) {
      return sortCityDataByArtistCount(list, artistCountByCity)
    }
    return list.sort((a, b) => b.population - a.population)
  }, [availableCities, artistCountByCity, filterCities])

  const shouldOptimize = availableCities.length > 500
  const POPULATION_THRESHOLD = 1000000

  const filteredCities = useMemo(() => {
    if (!searchQuery.trim()) {
      if (artistCountByCity) {
        return sortCityDataByArtistCount(availableCities, artistCountByCity)
      }
      if (shouldOptimize) {
        return availableCities
          .filter((cityData) => cityData.population > POPULATION_THRESHOLD)
          .sort((a, b) => b.population - a.population)
      }
      return [...availableCities].sort((a, b) => b.population - a.population)
    }

    const lowerQuery = searchQuery.toLowerCase()
    const firstLetter = lowerQuery.charAt(0)

    const citiesToShow = availableCities.filter((cityData) => {
      const cityLower = cityData.city.toLowerCase()
      const cityAsciiLower = cityData.cityAscii.toLowerCase()
      const countryLower = cityData.country.toLowerCase()

      const matchesQuery =
        cityLower.includes(lowerQuery) ||
        cityAsciiLower.includes(lowerQuery) ||
        countryLower.includes(lowerQuery)

      if (shouldOptimize) {
        const startsWithSameLetter =
          cityLower.startsWith(firstLetter) || cityAsciiLower.startsWith(firstLetter)
        const isPopular = cityData.population > POPULATION_THRESHOLD
        return matchesQuery || (startsWithSameLetter && isPopular)
      }

      return matchesQuery
    })

    return citiesToShow.sort((a, b) => {
      const aCityLower = a.city.toLowerCase()
      const aAsciiLower = a.cityAscii.toLowerCase()
      const bCityLower = b.city.toLowerCase()
      const bAsciiLower = b.cityAscii.toLowerCase()

      const aStartsWithQuery = aCityLower.startsWith(lowerQuery) || aAsciiLower.startsWith(lowerQuery)
      const bStartsWithQuery = bCityLower.startsWith(lowerQuery) || bAsciiLower.startsWith(lowerQuery)
      const aContainsQuery = aCityLower.includes(lowerQuery) || aAsciiLower.includes(lowerQuery)
      const bContainsQuery = bCityLower.includes(lowerQuery) || bAsciiLower.includes(lowerQuery)

      if (aStartsWithQuery && !bStartsWithQuery) return -1
      if (!aStartsWithQuery && bStartsWithQuery) return 1
      if (aContainsQuery && !bContainsQuery) return -1
      if (!aContainsQuery && bContainsQuery) return 1
      if (artistCountByCity) {
        return (artistCountByCity[b.city] ?? 0) - (artistCountByCity[a.city] ?? 0)
      }
      return b.population - a.population
    })
  }, [artistCountByCity, availableCities, searchQuery, shouldOptimize])

  const dropdownCities = useCompactDropdown ? compactCities : filteredCities

  const showSelectionCheckmarks = useSearchDropdown && !multiSelect

  const isCitySelected = (cityName: string) => {
    if (multiSelect) {
      return selectedCities.includes(cityName)
    }
    return value === cityName
  }

  const closeDropdown = () => {
    setIsDropdownOpen(false)
    setSearchQuery('')
  }

  const handleCitySelect = (cityData: CityData) => {
    if (multiSelect) {
      onMultiChange?.([cityData.city])
      closeDropdown()
      return
    }

    onChange?.(cityData.city, cityData)
    closeDropdown()
  }

  const triggerLabel = isLoadingCities ? 'Loading cities...' : value || placeholder
  const TriggerChevronIcon = isFilterSheetVariant ? ChevronDown : ChevronsUpDown

  const triggerClassName = isFilterSheetVariant
    ? cn(
        filterDropdownTriggerClassName,
        disabled || isLoadingCities ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      )
    : cn(
        'flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm transition-colors',
        disabled || isLoadingCities
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-pointer hover:bg-accent',
      )

  const dropdownPanelClassName = isFilterSheetVariant
    ? cn(
        filterLocationPanelClassName,
        layout === 'inline' ? 'mt-2' : 'absolute top-full left-0 right-0 z-[130] mt-2 shadow-lg',
      )
    : cn(
        'overflow-hidden rounded-lg border border-border bg-background shadow-lg',
        layout === 'inline' ? 'mt-2' : 'absolute top-full left-0 right-0 z-50 mt-1 w-full',
      )

  const listItemClassName = isFilterSheetVariant
    ? filterLocationPanelListItemClassName
    : 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-accent'

  const listMaxHeightClassName = isFilterSheetVariant
    ? 'max-h-[min(280px,40vh)] overflow-y-auto py-0.5'
    : 'max-h-64 overflow-y-auto'

  const usePortaledFilterSheetPopover = isFilterSheetVariant && layout === 'popover'

  const handleFilterSheetPopoverOpenChange = (open: boolean) => {
    if (disabled || isLoadingCities) return
    setIsDropdownOpen(open)
    if (!open) {
      setSearchQuery('')
    }
  }

  const triggerButton = (
    <button
      type="button"
      onClick={
        usePortaledFilterSheetPopover
          ? undefined
          : () => !disabled && !isLoadingCities && setIsDropdownOpen((open) => !open)
      }
      disabled={disabled || isLoadingCities}
      aria-expanded={isDropdownOpen}
      className={triggerClassName}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {showLocationIcon && <MapPin className={filterSearchBarIconClassName} aria-hidden />}
        <span className={isFilterSheetVariant ? filterDropdownValueClassName : 'truncate'}>
          {triggerLabel}
        </span>
      </span>
      <TriggerChevronIcon
        className={cn(
          isFilterSheetVariant
            ? `${filterSearchBarIconClassName} shrink-0 text-muted-foreground transition-transform`
            : 'ml-2 h-4 w-4 shrink-0 opacity-50',
          isFilterSheetVariant && isDropdownOpen && 'rotate-180',
        )}
      />
    </button>
  )

  const dropdownPanelBody = (
    <>
      {useSearchDropdown ? (
        <div className="relative border-b border-border/50">
          {isFilterSheetVariant ? (
            <FilterSearchInput
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.currentTarget.value)}
              placeholder={searchPlaceholder}
              className={filterLocationPanelSearchInputClassName}
            />
          ) : (
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={searchPlaceholder}
              className="rounded-none border-0 border-b border-border pr-8 text-sm focus:ring-0 focus:ring-offset-0"
              autoFocus
            />
          )}
          {searchQuery ? (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 hover:bg-muted/70"
            >
              <X className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      ) : null}

      <div className={cn(useCompactDropdown ? 'py-1' : listMaxHeightClassName)}>
        {dropdownCities.length === 0 ? (
          <div className="px-4 py-3 text-center text-sm text-muted-foreground">No city found.</div>
        ) : (
          dropdownCities.map((cityData) => {
            const selected = isCitySelected(cityData.city)
            return (
              <button
                key={`${cityData.city}-${cityData.country}`}
                type="button"
                onClick={() => handleCitySelect(cityData)}
                className={cn(
                  listItemClassName,
                  useCompactDropdown && !multiSelect && selected && 'bg-muted/70',
                )}
              >
                {showSelectionCheckmarks ? (
                  <Check
                    className={cn(
                      'h-3.5 w-3.5 shrink-0',
                      selected ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                ) : null}
                <div className="min-w-0 truncate">
                  <span className={cn(!multiSelect && selected && 'text-foreground')}>
                    {cityData.city}
                  </span>
                  {cityData.city !== cityData.country ? (
                    <span className="ml-1 text-muted-foreground">• {cityData.country}</span>
                  ) : null}
                </div>
              </button>
            )
          })
        )}
      </div>
    </>
  )

  if (usePortaledFilterSheetPopover) {
    return (
      <Popover open={isDropdownOpen} onOpenChange={handleFilterSheetPopoverOpenChange}>
        <div ref={dropdownRef} className={cn('relative', className)}>
          <PopoverTrigger asChild disabled={disabled || isLoadingCities}>
            {triggerButton}
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={filterSheetSelectContentSideOffset}
            className={cn(
              filterLocationPanelClassName,
              'z-[130] w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-trigger-width)] p-0 shadow-none',
            )}
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            {!isLoadingCities ? dropdownPanelBody : null}
          </PopoverContent>
        </div>
      </Popover>
    )
  }

  return (
    <div
      ref={dropdownRef}
      className={cn(layout === 'inline' ? 'flex flex-col' : 'relative', className)}
    >
      {triggerButton}

      {isDropdownOpen && !isLoadingCities ? (
        <div className={dropdownPanelClassName}>{dropdownPanelBody}</div>
      ) : null}
    </div>
  )
}
